import React, { useState, useEffect } from 'react';
import {
  MessageSquare, Eye, Bookmark, Share2, MoreHorizontal, Link2, Smile, ThumbsUp, ChevronDown, Plus, Search,
  Users, CheckCircle, Hash, ShieldCheck, Heart, ArrowUp, Star, Award, Paperclip, Edit, Trash2, X, Send
} from 'lucide-react';
import CourseHeader from "../components/CourseHeader";
import Footer from "../components/Footer";
import topBg from '../assets/images/top.png';
import { baseUrl, apiUrls } from '../utils/url';

export default function CommunityPage() {
  const [topContributors, setTopContributors] = useState([]);
  const [posts, setPosts] = useState([]);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [editingPost, setEditingPost] = useState(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [newPost, setNewPost] = useState({ title: '', body: '', category: 'General', tags: '' });
  const [expandedPostId, setExpandedPostId] = useState(null);
  const [postReplies, setPostReplies] = useState({});
  const [replyInputs, setReplyInputs] = useState({});
  const [activeReplyDropdown, setActiveReplyDropdown] = useState(null);
  const [editingReply, setEditingReply] = useState(null);
  const [stats, setStats] = useState(null);
  const [activeTab, setActiveTab] = useState('all');
  const [myPosts, setMyPosts] = useState([]);
  const [showAllPosts, setShowAllPosts] = useState(false);


  const getStatValue = (key, defaultValue) => {
    if (!stats) return defaultValue;
    if (key === 'members') {
      return stats.members ?? stats.memberCount ?? stats.totalMembers ?? stats.users ?? stats.userCount ?? defaultValue;
    }
    if (key === 'discussions') {
      return stats.discussions ?? stats.discussionCount ?? stats.posts ?? stats.postCount ?? stats.totalPosts ?? defaultValue;
    }
    if (key === 'replies') {
      return stats.replies ?? stats.replyCount ?? stats.totalReplies ?? defaultValue;
    }
    return defaultValue;
  };

  const token = localStorage.getItem('authToken');
  let currentUserId = null;
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    currentUserId = payload.userid;
    console.log('Current User ID from token:', currentUserId);
  } catch (e) {
    console.error('Error decoding token:', e);
  }

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.dropdown-container')) {
        setActiveDropdown(null);
        setActiveReplyDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleCreatePost = async (e) => {
    e.preventDefault();
    const url = 'https://wholesaleapi.ecomtamil.in/ecomcourse/community/createpost';
    const options = {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify(newPost)
    };

    try {
      const response = await fetch(url, options);
      const data = await response.json();
      console.log('Create Response:', data);
      if (data.status === 'S' || data.success) {
        setIsCreateModalOpen(false);
        setNewPost({ title: '', body: '', category: 'General', tags: '' });
        
        // Refetch posts to show the newly created one
        const fetchUrl = 'https://wholesaleapi.ecomtamil.in/ecomcourse/community/getposts';
        const fetchOptions = {
          method: 'POST',
          headers: {
            'content-type': 'application/json',
            Authorization: `Bearer ${token}`
          }
        };
        const res = await fetch(fetchUrl, fetchOptions);
        const fetchData = await res.json();
        if (fetchData && Array.isArray(fetchData.data)) {
          setPosts(fetchData.data);
        }
      }
    } catch (error) {
      console.error('Create error:', error);
    }
  };

  const handleDeletePost = async (postId) => {
    const url = 'https://wholesaleapi.ecomtamil.in/ecomcourse/community/deletepost';
    const options = {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({ postid: postId })
    };

    try {
      const response = await fetch(url, options);
      const data = await response.json();
      console.log('Delete Response:', data);
      if (data.status === 'S' || data.success) {
        setPosts(posts.filter(p => p.postid !== postId));
        setActiveDropdown(null);
      }
    } catch (error) {
      console.error('Delete error:', error);
    }
  };

  const handleUpdatePost = async (e) => {
    e.preventDefault();
    const url = 'https://wholesaleapi.ecomtamil.in/ecomcourse/community/updatepost';
    const options = {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({
        postid: editingPost.postid,
        title: editingPost.title,
        body: editingPost.body,
        category: editingPost.category || 'General',
        tags: editingPost.tags || 'community'
      })
    };

    try {
      const response = await fetch(url, options);
      const data = await response.json();
      console.log('Update Response:', data);
      if (data.status === 'S' || data.success) {
        setPosts(posts.map(p => p.postid === editingPost.postid ? { ...p, title: editingPost.title, body: editingPost.body } : p));
        setEditingPost(null);
      }
    } catch (error) {
      console.error('Update error:', error);
    }
  };

  const handleFetchReplies = async (postId) => {
    if (expandedPostId === postId) {
      setExpandedPostId(null);
      return;
    }
    
    setExpandedPostId(postId);
    
    if (!postReplies[postId]) {
      const url = 'https://wholesaleapi.ecomtamil.in/ecomcourse/community/getreplies';
      const options = {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ postid: postId })
      };

      try {
        const response = await fetch(url, options);
        const data = await response.json();
        console.log('Replies API Response:', data);
        if (data && Array.isArray(data.data)) {
          setPostReplies(prev => ({ ...prev, [postId]: data.data }));
        }
      } catch (error) {
        console.error('Fetch replies error:', error);
      }
    }
  };

  const handleCreateReply = async (e, postId) => {
    e.preventDefault();
    const content = replyInputs[postId];
    if (!content || !content.trim()) return;

    const url = 'https://wholesaleapi.ecomtamil.in/ecomcourse/community/createreply';
    const options = {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({ postid: postId, content: content.trim() })
    };

    try {
      const response = await fetch(url, options);
      const data = await response.json();
      console.log('Create Reply Response:', data);
      if (data.status === 'S' || data.success) {
        // Clear input
        setReplyInputs(prev => ({ ...prev, [postId]: '' }));
        // Refetch replies to update the list
        const fetchUrl = 'https://wholesaleapi.ecomtamil.in/ecomcourse/community/getreplies';
        const fetchOptions = {
          method: 'POST',
          headers: {
            'content-type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({ postid: postId })
        };
        const res = await fetch(fetchUrl, fetchOptions);
        const fetchData = await res.json();
        if (fetchData && Array.isArray(fetchData.data)) {
          setPostReplies(prev => ({ ...prev, [postId]: fetchData.data }));
          
          // Optimistically update reply count on the post
          setPosts(prevPosts => prevPosts.map(p => 
            p.postid === postId ? { ...p, replycount: (p.replycount || 0) + 1 } : p
          ));
        }
      }
    } catch (error) {
      console.error('Create reply error:', error);
    }
  };

  const handleDeleteReply = async (replyId, postId) => {
    const url = 'https://wholesaleapi.ecomtamil.in/ecomcourse/community/deletereply';
    const options = {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({ replyid: replyId })
    };

    try {
      const response = await fetch(url, options);
      const data = await response.json();
      console.log('Delete Reply Response:', data);
      if (data.status === 'S' || data.success) {
        setPostReplies(prev => ({
          ...prev,
          [postId]: prev[postId].filter(r => r.replyid !== replyId)
        }));
        setPosts(prevPosts => prevPosts.map(p =>
          p.postid === postId ? { ...p, replycount: Math.max((p.replycount || 1) - 1, 0) } : p
        ));
        setActiveReplyDropdown(null);
      }
    } catch (error) {
      console.error('Delete reply error:', error);
    }
  };

  const handleUpdateReply = async (e, postId) => {
    e.preventDefault();
    const url = 'https://wholesaleapi.ecomtamil.in/ecomcourse/community/updatereply';
    const options = {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({ replyid: editingReply.replyid, content: editingReply.content })
    };

    try {
      const response = await fetch(url, options);
      const data = await response.json();
      console.log('Update Reply Response:', data);
      if (data.status === 'S' || data.success) {
        setPostReplies(prev => ({
          ...prev,
          [postId]: prev[postId].map(r =>
            r.replyid === editingReply.replyid ? { ...r, content: editingReply.content } : r
          )
        }));
        setEditingReply(null);
      }
    } catch (error) {
      console.error('Update reply error:', error);
    }
  };

  const handleVoteReply = async (replyId, postId) => {
    const url = 'https://wholesaleapi.ecomtamil.in/community/votereply';
    const options = {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({ replyid: replyId })
    };

    try {
      const response = await fetch(url, options);
      const data = await response.json();
      console.log(data);
      if (data.status === 'S' || data.success) {
        if (data.upvotes !== undefined) {
          setPostReplies(prev => ({
            ...prev,
            [postId]: prev[postId].map(r =>
              r.replyid === replyId ? { ...r, upvotes: data.upvotes } : r
            )
          }));
        }
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleTabClick = (tab) => {
    setActiveTab(tab);
  };

  // Determine which posts to display based on active tab
  const displayPosts = activeTab === 'mypost' ? myPosts : posts;

  const handleVotePost = async (postId) => {
    const url = 'https://wholesaleapi.ecomtamil.in/ecomcourse/community/votepost';
    const options = {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({ postid: postId })
    };

    try {
      const response = await fetch(url, options);
      const data = await response.json();
      console.log('Vote Response:', data);
      if (data.status === 'S') {
        setPosts(prevPosts => prevPosts.map(p =>
          p.postid === postId ? { ...p, upvotes: data.upvotes } : p
        ));
      }
    } catch (error) {
      console.error('Vote error:', error);
    }
  };

  useEffect(() => {
    const fetchTopContributors = async () => {
      const url = 'https://wholesaleapi.ecomtamil.in/ecomcourse/community/gettopcontributors';
      const options = {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`
        }
      };

      try {
        const response = await fetch(url, options);
        const data = await response.json();
        console.log("Top Contributors API Response:", data);
        
        // Handle different possible response structures based on the provided screenshot
        if (data && Array.isArray(data.data)) {
          setTopContributors(data.data);
        } else if (Array.isArray(data)) {
          setTopContributors(data);
        } else {
            console.error("Unexpected data format from top contributors API", data);
        }
      } catch (error) {
        console.error("Error fetching top contributors:", error);
      }
    };

    const fetchPosts = async () => {
      const url = 'https://wholesaleapi.ecomtamil.in/ecomcourse/community/getposts';
      const options = {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      };

      try {
        const response = await fetch(url, options);
        const data = await response.json();
        console.log("Posts API Response:", data);
        if (data && Array.isArray(data.data)) {
          setPosts(data.data);
        }
      } catch (error) {
        console.error("Error fetching posts:", error);
      }
    };

    fetchTopContributors();
    fetchPosts();

    // Fetch My Posts on initial load
    const fetchMyPostsInitial = async () => {
      const url = `${baseUrl}${apiUrls.getmyposts}`;
      const options = {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`
        }
      };

      try {
        const response = await fetch(url, options);
        const data = await response.json();
        console.log('My Posts API Response:', data);
        if (data && Array.isArray(data.data)) {
          setMyPosts(data.data);
        }
      } catch (error) {
        console.error('Error fetching my posts:', error);
      }
    };

    fetchMyPostsInitial();

    const fetchDashboardStats = async () => {
      const url = `${baseUrl}${apiUrls.getcommunitydashboardstats}`;
      const authHeader = token ? `Bearer ${token}` : 'Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1c2VyaWQiOjIwMywiZW1haWwiOiJyYWphQGdtYWlsLmNvbSIsInVzZXJ0eXBlIjoyLCJpYXQiOjE3ODQ3MDI3NTYsImV4cCI6MTc4NDc0NTk1Nn0.gdqzA_OGNfD-kXVHaY-QwICCgMAgPfBGazzPMeRuThA';
      const options = {
        method: 'POST',
        headers: {
          Authorization: authHeader
        }
      };

      try {
        const response = await fetch(url, options);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          const data = await response.json();
          console.log(data);
          const statsData = data.data || data;
          setStats(statsData);
        } else {
          console.warn('Dashboard stats response is not JSON.');
        }
      } catch (error) {
        console.error(error);
      }
    };

    fetchDashboardStats();
  }, []);

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col pt-16">
      <CourseHeader />

      {isCreateModalOpen && (
        <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg  overflow-hidden flex flex-col">
            <div className="p-4 border-b border-slate-100 flex items-center justify-between bg-slate-50">
              <h2 className="text-lg font-bold text-slate-800">Create New Post</h2>
              <button 
                onClick={() => setIsCreateModalOpen(false)}
                className="text-slate-400 hover:text-slate-600 p-1 rounded-md transition-colors"
              >
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleCreatePost} className="p-5 flex flex-col gap-4">
              <div>
                <label className="block text-xs font-bold text-slate-600 mb-1">Title</label>
                <input 
                  type="text" 
                  value={newPost.title}
                  onChange={(e) => setNewPost({...newPost, title: e.target.value})}
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="What's your question or topic?"
                  required
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-600 mb-1">Body</label>
                <textarea 
                  value={newPost.body}
                  onChange={(e) => setNewPost({...newPost, body: e.target.value})}
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 min-h-[120px]"
                  placeholder="Share more details..."
                  required
                />
              </div>
              <div className="flex gap-4">
                <div className="flex-1">
                  <label className="block text-xs font-bold text-slate-600 mb-1">Category</label>
                  <select 
                    value={newPost.category}
                    onChange={(e) => setNewPost({...newPost, category: e.target.value})}
                    className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white"
                  >
                    <option value="General">General</option>
                    <option value="Programming">Programming</option>
                    <option value="Strategy">Strategy</option>
                    <option value="Marketing">Marketing</option>
                  </select>
                </div>
                <div className="flex-1">
                  <label className="block text-xs font-bold text-slate-600 mb-1">Tags</label>
                  <input 
                    type="text" 
                    value={newPost.tags}
                    onChange={(e) => setNewPost({...newPost, tags: e.target.value})}
                    className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="e.g. nodejs, express"
                  />
                </div>
              </div>
              <div className="pt-4 flex justify-end gap-2 border-t border-slate-100 mt-2">
                <button 
                  type="button"
                  onClick={() => setIsCreateModalOpen(false)}
                  className="px-4 py-2 text-sm font-bold text-slate-600 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors"
                >
                  Cancel
                </button>
                <button 
                  type="submit"
                  className="px-4 py-2 text-sm font-bold text-white bg-indigo-500 hover:bg-indigo-600 rounded-lg transition-colors"
                >
                  Post
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="flex-grow pb-16 px-4 max-w-[1400px] mx-auto w-full">
        {/* Top Banner */}
        <div className="bg-orange-50/50 rounded-2xl md:rounded-[2rem] p-4 sm:p-6 md:p-8 mb-6 mt-4 sm:mt-6 relative overflow-hidden border border-orange-100/50">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4 sm:mb-6 z-10 relative">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-slate-800 mb-1 sm:mb-2">Community</h1>
              <p className="text-xs sm:text-sm text-slate-600 font-medium">Share, learn & grow together with the Ecom Tamil community.</p>
            </div>
            {/* Mobile Only New Post Button */}
            <button 
              onClick={() => setIsCreateModalOpen(true)}
              className="md:hidden bg-indigo-500 hover:bg-indigo-600 text-white font-bold px-4 py-2.5 rounded-xl flex items-center justify-center gap-2 text-xs transition-transform hover:scale-105  self-end shrink-0"
            >
              <Plus size={16} />
              <span>New Post</span>
            </button>
          </div>

          {/* Stats Boxes */}
          <div className="grid grid-cols-3 gap-2 sm:gap-4 max-w-md sm:max-w-lg z-10 relative">
            <div className="bg-white rounded-xl sm:rounded-2xl p-2.5 sm:p-3 flex flex-col sm:flex-row items-center gap-1.5 sm:gap-3 border border-slate-100 text-center sm:text-left">
              <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-purple-100 flex items-center justify-center text-purple-600 shrink-0">
                <Users size={16} className="sm:w-[18px] sm:h-[18px]" />
              </div>
              <div className="min-w-0">
                <p className="text-sm sm:text-lg font-bold text-slate-800 leading-tight">{getStatValue('members', 128)}</p>
                <p className="text-[10px] sm:text-xs text-slate-500 font-medium truncate">Members</p>
              </div>
            </div>
            
            <div className="bg-white rounded-xl sm:rounded-2xl p-2.5 sm:p-3 flex flex-col sm:flex-row items-center gap-1.5 sm:gap-3 border border-slate-100 text-center sm:text-left">
              <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-600 shrink-0">
                <MessageSquare size={16} className="sm:w-[18px] sm:h-[18px]" />
              </div>
              <div className="min-w-0">
                <p className="text-sm sm:text-lg font-bold text-slate-800 leading-tight">{getStatValue('discussions', 56)}</p>
                <p className="text-[10px] sm:text-xs text-slate-500 font-medium truncate">Discussions</p>
              </div>
            </div>

            <div className="bg-white rounded-xl sm:rounded-2xl p-2.5 sm:p-3 flex flex-col sm:flex-row items-center gap-1.5 sm:gap-3 border border-slate-100 text-center sm:text-left">
              <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-amber-100 flex items-center justify-center text-amber-600 shrink-0">
                <Star size={16} className="sm:w-[18px] sm:h-[18px]" />
              </div>
              <div className="min-w-0">
                <p className="text-sm sm:text-lg font-bold text-slate-800 leading-tight">{getStatValue('replies', 320)}</p>
                <p className="text-[10px] sm:text-xs text-slate-500 font-medium truncate">Replies</p>
              </div>
            </div>
          </div>

          {/* Desktop Right Side: Graphic + New Post Button side-by-side */}
          <div className="hidden md:flex absolute bottom-6 right-6 lg:right-8 items-end gap-4 lg:gap-6 z-20">
             <div className="h-[120px] lg:h-[150px]">
               <img src={topBg} alt="Community Graphic" className="h-full object-contain mix-blend-multiply" />
             </div>
             <button 
               onClick={() => setIsCreateModalOpen(true)}
               className="bg-indigo-500 hover:bg-indigo-600 text-white font-bold px-6 py-3 rounded-xl flex items-center gap-2 transition-transform hover:scale-105 shrink-0 mb-2"
             >
               <Plus size={18} />
               <span>New Post</span>
             </button>
          </div>
        </div>

        {/* Main Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          
          {/* Left Sidebar */}
          <div className="lg:col-span-3">

            {/* My Posts List */}
            <div className="bg-white rounded-2xl p-4 border border-slate-100 flex flex-col">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-bold text-slate-800 text-sm flex items-center gap-2">
                  <span className="text-orange-500">🔥</span> My Discussions
                </h3>
                <span className="bg-indigo-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-lg">
                  {myPosts.length}
                </span>
              </div>

              <div className="space-y-1 overflow-y-auto pr-1 flex-1">
                {myPosts.length > 0 ? (
                  myPosts.map((post, index) => {
                    const initial = post.username ? post.username.charAt(0).toUpperCase() : 'U';
                    const avatarColors = [
                      'bg-emerald-500', 'bg-blue-500', 'bg-purple-500',
                      'bg-orange-400', 'bg-pink-500', 'bg-teal-500', 'bg-indigo-500'
                    ];
                    const avatarColor = avatarColors[index % avatarColors.length];

                    const categoryStyles = {
                      General:   { text: 'text-indigo-500',  bg: 'bg-indigo-50'  },
                      Business:  { text: 'text-orange-500',  bg: 'bg-orange-50'  },
                      Strategy:  { text: 'text-blue-500',    bg: 'bg-blue-50'    },
                      Marketing: { text: 'text-emerald-500', bg: 'bg-emerald-50' },
                    };
                    const catStyle = categoryStyles[post.category] || { text: 'text-slate-500', bg: 'bg-slate-100' };

                    return (
                      <div
                        key={post.postid}
                        className={`p-3 rounded-xl cursor-pointer transition-colors border border-transparent ${index === 0 ? 'bg-slate-50 hover:bg-indigo-50/50 hover:border-indigo-100' : 'hover:bg-slate-50'}`}
                      >
                        <div className="flex items-start gap-3">
                          <div className={`w-8 h-8 rounded-full ${avatarColor} text-white font-bold flex items-center justify-center text-xs shrink-0`}>
                            {initial}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex justify-between items-start mb-1">
                              <h4 className="font-bold text-slate-800 text-sm truncate">{post.title}</h4>
                              {post.category && (
                                <span className={`text-[9px] font-bold ${catStyle.text} ${catStyle.bg} px-1.5 py-0.5 rounded uppercase ml-1 shrink-0`}>
                                  {post.category}
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-slate-500 truncate">{post.body}</p>
                            <div className="flex items-center gap-3 mt-2 text-[10px] text-slate-400 font-semibold">
                              <span className="flex items-center gap-1"><ThumbsUp size={12} /> {post.upvotes || 0}</span>
                              <span className="flex items-center gap-1"><MessageSquare size={12} /> {post.replycount || 0}</span>
                              <span className="flex items-center gap-1"><Eye size={12} /> {post.views || 0}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="flex flex-col items-center justify-center py-8 text-center">
                    <MessageSquare size={28} className="text-slate-200 mb-2" />
                    <p className="text-xs text-slate-400 font-medium">No posts yet</p>
                    <p className="text-[10px] text-slate-300 mt-1">Your posts will appear here</p>
                  </div>
                )}
              </div>

              <div className="mt-4 pt-4 border-t border-slate-100 flex justify-center">
                 <button
                   onClick={() => handleTabClick('mypost')}
                   className="text-slate-500 hover:text-indigo-600 font-bold text-xs flex items-center gap-1 transition-colors"
                 >
                    View All My Posts &rarr;
                 </button>
              </div>
            </div>
          </div>

          {/* Main Content Area */}
          <div className="lg:col-span-6 space-y-4">
            
            {/* Posts Feed */}
            {(() => {
              const visiblePosts = showAllPosts ? posts : posts.slice(0, 3);
              return (
                <>
                  {visiblePosts.length > 0 ? (
                    visiblePosts.map((post) => {
                        const initial = post.username ? post.username.charAt(0).toUpperCase() : 'U';
                        const postDate = new Date(post.created_at);
                        const timeAgo = !isNaN(postDate) ? postDate.toLocaleDateString() : 'Recently';

                        return (
                            <div key={post.postid} className="bg-white rounded-2xl border border-slate-100 overflow-hidden relative mb-4">
                                <div className="p-4 sm:p-6">
                                    <div className="flex items-start justify-between mb-4">
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 rounded-full bg-emerald-500 text-white font-bold flex items-center justify-center text-sm shrink-0">
                                                {initial}
                                            </div>
                                            <div className="min-w-0">
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    <h3 className="font-bold text-slate-800 text-sm truncate">{post.username}</h3>
                                                    {post.category && (
                                                        <span className="bg-indigo-50 text-indigo-600 border border-indigo-100 text-[9px] font-bold px-1.5 py-0.5 rounded uppercase">{post.category}</span>
                                                    )}
                                                </div>
                                                <p className="text-xs text-slate-400 font-medium">{timeAgo}</p>
                                            </div>
                                        </div>
                                        {String(post.userid) === String(currentUserId) && (
                                        <div className="relative">
                                            <button 
                                                onClick={() => setActiveDropdown(activeDropdown === post.postid ? null : post.postid)}
                                                className="text-slate-400 hover:text-slate-600 p-1 rounded-md transition-colors"
                                            >
                                                <MoreHorizontal size={18} />
                                            </button>
                                            {activeDropdown === post.postid && (
                                                <div className="absolute right-0 mt-2 w-36 bg-white rounded-xl border border-slate-100 py-1 z-10">
                                                    <button 
                                                        onClick={() => { setEditingPost(post); setActiveDropdown(null); }}
                                                        className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2"
                                                    >
                                                        <Edit size={14} /> Edit
                                                    </button>
                                                    <button 
                                                        onClick={() => handleDeletePost(post.postid)}
                                                        className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                                                    >
                                                        <Trash2 size={14} /> Delete
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                        )}
                                    </div>

                                    {editingPost && editingPost.postid === post.postid ? (
                                        <form onSubmit={handleUpdatePost} className="mb-6 space-y-3">
                                            <input 
                                                type="text" 
                                                value={editingPost.title}
                                                onChange={(e) => setEditingPost({...editingPost, title: e.target.value})}
                                                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm font-bold text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                                required
                                            />
                                            <textarea 
                                                value={editingPost.body}
                                                onChange={(e) => setEditingPost({...editingPost, body: e.target.value})}
                                                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 min-h-[100px]"
                                                required
                                            />
                                            <div className="flex justify-end gap-2">
                                                <button 
                                                    type="button" 
                                                    onClick={() => setEditingPost(null)}
                                                    className="px-4 py-2 text-sm font-bold text-slate-600 bg-slate-100 rounded-lg hover:bg-slate-200"
                                                >
                                                    Cancel
                                                </button>
                                                <button 
                                                    type="submit"
                                                    className="px-4 py-2 text-sm font-bold text-white bg-indigo-500 rounded-lg hover:bg-indigo-600"
                                                >
                                                    Save Changes
                                                </button>
                                            </div>
                                        </form>
                                    ) : (
                                        <>
                                            <h2 className="text-lg sm:text-xl font-bold text-slate-900 mb-2 break-words">{post.title}</h2>
                                            <p className="text-slate-600 text-sm mb-6 break-words">{post.body}</p>
                                        </>
                                    )}

                                    <div className="flex flex-wrap items-center gap-2 sm:gap-3 pt-4 border-t border-slate-50 text-[11px] sm:text-xs font-bold">
                                        <button 
                                            onClick={() => handleVotePost(post.postid)}
                                            className="flex items-center gap-1.5 text-indigo-600 bg-indigo-50 px-2.5 sm:px-3 py-1.5 rounded-lg border border-indigo-100 hover:bg-indigo-100 transition-colors cursor-pointer"
                                        >
                                            <ThumbsUp size={14} className="fill-current" /> {post.upvotes || 0} Likes
                                        </button>
                                        <button 
                                            onClick={() => handleFetchReplies(post.postid)}
                                            className="flex items-center gap-1.5 text-slate-500 border border-slate-100 px-2.5 sm:px-3 py-1.5 rounded-lg hover:bg-slate-50 transition-colors"
                                        >
                                            <MessageSquare size={14} /> {post.replycount || 0} Replies
                                        </button>
                                        <span className="flex items-center gap-1 text-slate-400">
                                            <Eye size={14} /> {post.views || 0} Views
                                        </span>
                                        <div className="flex-1 flex justify-end gap-2 text-slate-500">
                                            <button className="flex items-center gap-1 hover:text-indigo-500">
                                                <Bookmark size={14} /> Save
                                            </button>
                                            <button className="flex items-center gap-1 hover:text-indigo-500 ml-2">
                                                <Share2 size={14} /> Share
                                            </button>
                                        </div>
                                    </div>

                                    {/* Replies Section */}
                                    {expandedPostId === post.postid && (
                                        <div className="mt-4 pt-4 border-t border-slate-100">
                                            {postReplies[post.postid] ? (
                                                postReplies[post.postid].length > 0 ? (
                                                    <div className="space-y-4">
                                                        {postReplies[post.postid].map((reply, idx) => (
                                                            <div key={idx} className="flex gap-3 bg-slate-50 p-3 rounded-xl border border-slate-100">
                                                                <div className="w-8 h-8 rounded-full bg-indigo-100 text-indigo-600 font-bold flex items-center justify-center text-xs shrink-0">
                                                                    {reply.username ? reply.username.charAt(0).toUpperCase() : 'U'}
                                                                </div>
                                                                <div className="flex-1 min-w-0">
                                                                    <div className="flex items-center justify-between mb-1">
                                                                        <div className="flex items-center gap-2">
                                                                            <h4 className="font-bold text-slate-800 text-xs">{reply.username}</h4>
                                                                            <span className="text-[10px] text-slate-400">{new Date(reply.created_at).toLocaleString()}</span>
                                                                        </div>
                                                                        {String(reply.userid) === String(currentUserId) && (
                                                                        <div className="relative">
                                                                            <button
                                                                                onClick={() => setActiveReplyDropdown(activeReplyDropdown === reply.replyid ? null : reply.replyid)}
                                                                                className="text-slate-400 hover:text-slate-600 p-0.5 rounded transition-colors"
                                                                            >
                                                                                <MoreHorizontal size={14} />
                                                                            </button>
                                                                            {activeReplyDropdown === reply.replyid && (
                                                                                <div className="absolute right-0 mt-1 w-32 bg-white rounded-xl border border-slate-100 py-1 z-20">
                                                                                    <button
                                                                                        onClick={() => { setEditingReply({ ...reply }); setActiveReplyDropdown(null); }}
                                                                                        className="w-full text-left px-3 py-1.5 text-xs text-slate-700 hover:bg-slate-50 flex items-center gap-2"
                                                                                    >
                                                                                        <Edit size={12} /> Edit
                                                                                    </button>
                                                                                    <button
                                                                                        onClick={() => handleDeleteReply(reply.replyid, post.postid)}
                                                                                        className="w-full text-left px-3 py-1.5 text-xs text-red-600 hover:bg-red-50 flex items-center gap-2"
                                                                                    >
                                                                                        <Trash2 size={12} /> Delete
                                                                                    </button>
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                        )}
                                                                    </div>
                                                                    {editingReply && editingReply.replyid === reply.replyid ? (
                                                                        <form onSubmit={(e) => handleUpdateReply(e, post.postid)} className="mt-1 flex gap-2">
                                                                            <input
                                                                                type="text"
                                                                                value={editingReply.content}
                                                                                onChange={(e) => setEditingReply({ ...editingReply, content: e.target.value })}
                                                                                className="flex-1 border border-slate-200 rounded-lg px-2 py-1 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                                                                required
                                                                            />
                                                                            <button type="submit" className="text-xs font-bold text-white bg-indigo-500 hover:bg-indigo-600 px-3 py-1 rounded-lg">Save</button>
                                                                            <button type="button" onClick={() => setEditingReply(null)} className="text-xs font-bold text-slate-600 bg-slate-100 hover:bg-slate-200 px-3 py-1 rounded-lg">Cancel</button>
                                                                        </form>
                                                                    ) : (
                                                                        <div>
                                                                            <p className="text-sm text-slate-700 mt-1 leading-relaxed break-words">{reply.content}</p>
                                                                            <div className="mt-2 flex items-center gap-2">
                                                                                <button 
                                                                                    onClick={() => handleVoteReply(reply.replyid, post.postid)}
                                                                                    className="text-xs text-slate-500 hover:text-indigo-600 flex items-center gap-1 font-medium transition-colors"
                                                                                >
                                                                                    <ThumbsUp size={12} /> Like {reply.upvotes ? `(${reply.upvotes})` : ''}
                                                                                </button>
                                                                            </div>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                ) : (
                                                    <p className="text-sm text-slate-500 text-center py-2">No replies yet.</p>
                                                )
                                            ) : (
                                                <p className="text-sm text-slate-500 text-center py-2">Loading replies...</p>
                                            )}

                                            {/* Reply Input Form */}
                                            <form onSubmit={(e) => handleCreateReply(e, post.postid)} className="mt-4 flex gap-2">
                                                <input 
                                                    type="text"
                                                    value={replyInputs[post.postid] || ''}
                                                    onChange={(e) => setReplyInputs({...replyInputs, [post.postid]: e.target.value})}
                                                    placeholder="Write a reply..."
                                                    className="flex-1 border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                                />
                                                <button 
                                                    type="submit" 
                                                    disabled={!replyInputs[post.postid]?.trim()}
                                                    className="bg-indigo-500 hover:bg-indigo-600 disabled:bg-slate-300 disabled:cursor-not-allowed text-white p-2 rounded-lg transition-colors flex items-center justify-center"
                                                >
                                                    <Send size={16} />
                                                </button>
                                            </form>
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })
                  ) : (
                    <div className="text-center py-10 text-slate-500 text-sm font-medium">Loading posts...</div>
                  )}

                  {/* View More / Show Less Button */}
                  {posts.length > 3 && (
                    <div className="flex justify-center mt-2 mb-4">
                      <button
                        onClick={() => setShowAllPosts(!showAllPosts)}
                        className="flex items-center gap-2 text-sm font-bold text-indigo-600 bg-indigo-50 hover:bg-indigo-100 border border-indigo-100 px-6 py-2.5 rounded-xl transition-all"
                      >
                        {showAllPosts ? (
                          <><ChevronDown size={16} className="rotate-180 transition-transform" /> Show Less</>
                        ) : (
                          <><ChevronDown size={16} className="transition-transform" /> View More ({posts.length - 3} more)</>
                        )}
                      </button>
                    </div>
                  )}
                </>
              );
            })()}

          </div>

          {/* Right Sidebar */}
          <div className="lg:col-span-3 space-y-6">
            
            {/* Guidelines */}
            <div className="bg-white rounded-2xl p-5 border border-slate-100">
                <h3 className="font-bold text-slate-800 text-sm mb-4 flex items-center gap-2">
                    <ShieldCheck size={16} className="text-indigo-500" /> Community Guidelines
                </h3>
                <ul className="space-y-3 text-xs font-medium text-slate-600">
                    <li className="flex items-center gap-2"><Heart size={14} className="text-slate-400" /> Be respectful and kind</li>
                    <li className="flex items-center gap-2"><MessageSquare size={14} className="text-slate-400" /> Stay on topic</li>
                    <li className="flex items-center gap-2"><Share2 size={14} className="text-slate-400" /> No self-promotion</li>
                    <li className="flex items-center gap-2"><Users size={14} className="text-slate-400" /> Share knowledge</li>
                    <li className="flex items-center gap-2"><ArrowUp size={14} className="text-slate-400" /> Help others grow</li>
                </ul>
            </div>

            {/* Popular Tags */}
            <div className="bg-white rounded-2xl p-5 border border-slate-100">
                <h3 className="font-bold text-slate-800 text-sm mb-4 flex items-center gap-2">
                    <Hash size={16} className="text-indigo-500" /> Popular Tags
                </h3>
                <div className="flex flex-wrap gap-2">
                    <span className="bg-indigo-50 text-indigo-600 text-[10px] font-bold px-2 py-1 rounded-md border border-indigo-100 cursor-pointer hover:bg-indigo-100 transition-colors">Meesho</span>
                    <span className="bg-emerald-50 text-emerald-600 text-[10px] font-bold px-2 py-1 rounded-md border border-emerald-100 cursor-pointer hover:bg-emerald-100 transition-colors">Marketing</span>
                    <span className="bg-blue-50 text-blue-600 text-[10px] font-bold px-2 py-1 rounded-md border border-blue-100 cursor-pointer hover:bg-blue-100 transition-colors">Shipping</span>
                    <span className="bg-orange-50 text-orange-600 text-[10px] font-bold px-2 py-1 rounded-md border border-orange-100 cursor-pointer hover:bg-orange-100 transition-colors">Business</span>
                    <span className="bg-purple-50 text-purple-600 text-[10px] font-bold px-2 py-1 rounded-md border border-purple-100 cursor-pointer hover:bg-purple-100 transition-colors">Strategy</span>
                    <span className="bg-pink-50 text-pink-600 text-[10px] font-bold px-2 py-1 rounded-md border border-pink-100 cursor-pointer hover:bg-pink-100 transition-colors">Inventory</span>
                </div>
            </div>

            {/* Top Contributors */}
            <div className="bg-white rounded-2xl p-5 border border-slate-100">
                <h3 className="font-bold text-slate-800 text-sm mb-4 flex items-center gap-2">
                    <Award size={16} className="text-orange-500" /> Top Contributors
                </h3>
                
                <div className="space-y-4">
                    {topContributors.length > 0 ? (
                        topContributors.map((contributor, index) => {
                            // Extract username and points based on the actual API response
                            const username = contributor.username || `User ${index + 1}`;
                            const initial = username.charAt(0).toUpperCase();
                            const score = contributor.points || 0;
                            
                            // Determine colors based on rank
                            let badgeColor = "bg-slate-300";
                            let avatarColor = "bg-slate-500";
                            
                            if (index === 0) {
                                badgeColor = "bg-amber-400";
                                avatarColor = "bg-blue-500";
                            } else if (index === 1) {
                                badgeColor = "bg-slate-300";
                                avatarColor = "bg-orange-500";
                            } else if (index === 2) {
                                badgeColor = "bg-orange-300";
                                avatarColor = "bg-teal-500";
                            }

                            return (
                                <div key={index} className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                        <div className={`w-8 h-8 rounded-full ${avatarColor} text-white font-bold flex items-center justify-center text-xs shrink-0`}>
                                            {initial}
                                        </div>
                                        <div>
                                            <h4 className="font-bold text-slate-800 text-xs">{username}</h4>
                                            <p className="text-[10px] text-slate-500 font-medium">{score} Points</p>
                                        </div>
                                    </div>
                                    <div className={`w-5 h-5 rounded-full ${badgeColor} flex items-center justify-center text-white font-bold text-[10px]`}>
                                        {index + 1}
                                    </div>
                                </div>
                            );
                        })
                    ) : (
                        <p className="text-xs text-slate-500 text-center py-2">Loading top contributors...</p>
                    )}
                </div>


            </div>

          </div>

        </div>
      </div>
      <Footer />
    </div>
  );
}
